<script lang="ts">
	import { onMount } from 'svelte';
	import { _ } from 'svelte-i18n';
	import type { Tag } from '$lib/api/restApi';

	import { getHubStores } from '$lib/stores/hubStores';
	import { clickOutside } from '$lib/actions/clickOutside';

	const { hub, restApi } = getHubStores();

	export let addedTags: string[];

	let preDefinedTags: Tag[] = [];
	let filteredTags: Tag[] = [];

	onMount(async () => {
		const resp = await restApi.fetchTags($hub.id);
		if (resp.data) preDefinedTags = resp.data;
	});

	let value = '';
	$: valueWhiteoutHash = value.replace(/^#/, '');

	$: showAddTag = false;
	let hoveredTag: null | string = null;

	const handleChange = () => {
		if (!valueWhiteoutHash) {
			showAddTag = false;
			filteredTags = [];
			return;
		}
		filteredTags = preDefinedTags.filter(
			(tag) => tag.name?.includes(valueWhiteoutHash) && !addedTags.includes(tag.name)
		);
		if (
			filteredTags.some((tag) => tag.name === valueWhiteoutHash) ||
			addedTags.includes(valueWhiteoutHash)
		) {
			showAddTag = false;
		} else {
			showAddTag = true;
		}
	};

	const addTag = (withMouse = true) => {
		// TODO: This logic will not work on tablets, need to figure out a way to bypass the handleFocusOut
		// event when "clicking" on a tag from the menu. The withMouse param is used only if you are in the
		// input field and using the enter key
		const tag = hoveredTag && withMouse ? hoveredTag : valueWhiteoutHash;
		hoveredTag = null;
		if (!addedTags.includes(tag)) {
			addedTags = [...addedTags, tag];
		}
		value = '';
		filteredTags = [];
		showAddTag = false;
	};

	const removeTag = (i = addedTags.length - 1) => {
		addedTags.splice(i, 1);
		addedTags = [...addedTags];
		unSelectTags();
	};

	let focus = false;

	const handleMouseLeave = () => {
		if (!value) focus = false;
	};

	const handleFocusOut = () => {
		focus = false;
		if (value) addTag();
	};

	let selectedTag: null | number = null;
	const globalRemoveTagListener = (event: KeyboardEvent) => {
		if (event.key == 'Backspace' && selectedTag !== null) {
			removeTag(selectedTag);
		}
	};

	const unSelectTags = () => {
		removeEventListener('pointerdown', unSelectTags);
		if (selectedTag !== null) {
			removeEventListener('keydown', globalRemoveTagListener);
			const tagElm = document.getElementById(`addedTag${selectedTag}`);
			if (tagElm) tagElm.style.background = 'lightgrey';
		}
		selectedTag = null;
	};

	const selectTag = (i: number) => {
		unSelectTags();
		selectedTag = i;
		const tagElm = document.getElementById(`addedTag${i}`);
		if (tagElm) tagElm.style.background = 'rgb(12 142 240)';
		document.addEventListener('keydown', globalRemoveTagListener);
		document.addEventListener('pointerdown', unSelectTags);
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		if (event.key == 'Backspace' && !value) removeTag();
		if (event.key == 'Enter') {
			event.preventDefault();
			addTag(false);
		}
	};

	const handleClickOutside = () => {
		filteredTags = [];
		showAddTag = false;
		value = '';
		focus = false;
	};
</script>

<div id="container" use:clickOutside={handleClickOutside}>
	<div id="innerContainer">
		<!-- svelte-ignore a11y-no-static-element-interactions -->
		<div
			id="menu"
			class:focus
			on:mouseenter={() => {
				focus = true;
			}}
			on:focusout={handleFocusOut}>
			{#if filteredTags.length || (addedTags && addedTags.length) || showAddTag}
				<div id="innerMenu">
					{#if showAddTag}
						<button type="button" id="addTag" on:click={() => addTag()}>
							{$_('common.add')}<strong>{` #${valueWhiteoutHash}`}</strong>
						</button>
					{/if}
					{#if filteredTags.length}
						{#each filteredTags as tag}
							<button
								type="button"
								class="tagOption"
								on:mouseenter={() => {
									hoveredTag = tag.name;
								}}
								on:mouseleave={() => {
									hoveredTag = null;
								}}
								on:click={() => addTag()}>
								<span>#{tag.name}</span><span class="tagCount">({tag.count})</span>
							</button>
						{/each}
					{/if}
					<div id="addedTags" class:focus>
						{#each addedTags as tag, i}
							<button
								type="button"
								on:click={() => selectTag(i)}
								id={`addedTag${i}`}
								class="addedTag">#{tag}</button>
						{/each}
					</div>
				</div>
			{/if}
		</div>
		<input
			class:focus
			bind:value
			on:mouseleave={handleMouseLeave}
			on:input={handleChange}
			on:keydown={handleKeyDown}
			placeholder={$_('tag.manager.add.tag.placeholder')} />
	</div>
</div>

<style>
	button {
		padding: 0;
		border-width: 0;
		background-color: transparent;
		font-family: inherit;
		font-size: inherit;
		font-style: inherit;
		font-weight: inherit;
		line-height: inherit;
	}

	#container {
		position: relative;
	}

	#innerContainer {
		inset-block-start: 0;
	}

	input {
		block-size: 36px;
		inline-size: 200px;
		padding-inline: 5px;
		border: 0;
	}

	#menu {
		display: flex;
		position: absolute;
		align-items: center;
		min-block-size: 36px;
		inline-size: 100%;
		border: rgb(195 195 195) solid 1px;
		border-radius: var(--gutterSmaller);
		cursor: pointer;
		inset-block-end: 0;
	}

	#menu.focus {
		background-color: white;
	}

	#innerMenu {
		display: flex;
		flex-direction: column;
		inline-size: 100%;
		padding: var(--gutterSmaller);
		overflow: hidden;
		background-color: white;
	}

	.tagOption,
	#addTag {
		padding: 10px;
		color: rgb(76 83 93);
	}

	#addTag {
		border-block-end: rgb(195 195 195) solid 2px;
	}

	.tagOption:hover,
	#addTag:hover {
		background-color: lightgrey;
	}

	.tagCount {
		margin-inline-start: 5px;
		color: grey;
	}

	input.focus {
		border: rgb(195 195 195) solid 1px;
		border-radius: var(--gutterSmaller);
		isolation: isolate;
	}

	#addedTags {
		display: flex;
		align-items: center;
		max-inline-size: 170px;
	}

	#addedTags.focus {
		flex-wrap: wrap;
		margin-block-end: 36px;
	}

	#addedTags.focus > * {
		margin-block-start: 5px;
	}

	.addedTag {
		max-inline-size: 170px;
		margin-inline-end: 5px;
		padding: 3px;
		border-radius: 5px;
		background: lightgrey;
		font-size: small;
		text-overflow: ellipsis;
	}
</style>
